import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Badge,
  Button,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Event,
  AttachMoney,
  Warning,
} from '@mui/icons-material';
import { addMonths, subMonths, format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addYears } from 'date-fns';
import { useQuery, useQueryClient } from 'react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getCategoryColor, CATEGORIES } from '../utils/categoryColors';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { formatCurrency, getMonthlyCostInCurrency } from '../utils/currency';
import CalendarDateModal from '../components/CalendarDateModal';
import ServiceLogo from '../components/ServiceLogo';
import { calculateNextRenewalDate, BillingCycle } from '../utils/billingCycleUtils';

const Calendar: React.FC = () => {
  const { user } = useAuth();
  const { getMainCurrency } = useUserPreferences(user?.id);
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDateSubscriptions, setSelectedDateSubscriptions] = useState<any[]>([]);

  const { data: subscriptions } = useQuery(
    ['subscriptions', user?.id],
    async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('next_renewal_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    {
      enabled: !!user,
    }
  );

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calculate the offset to align days with the correct weekdays
  const startDayOfWeek = monthStart.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const calendarDays = [];
  
  // Add empty cells for days before the month starts
  for (let i = 0; i < startDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add all days of the month
  calendarDays.push(...daysInMonth);
  
  // Add empty cells to complete the last week (if needed)
  const remainingCells = 7 - (calendarDays.length % 7);
  if (remainingCells < 7) {
    for (let i = 0; i < remainingCells; i++) {
      calendarDays.push(null);
    }
  }

  const getSubscriptionsForDay = (date: Date) => {
    if (!subscriptions) return [];
    
    const subscriptionsForDay: any[] = [];
    
    subscriptions.forEach(sub => {
      // Check if this is the next renewal date
      if (isSameDay(new Date(sub.next_renewal_date), date)) {
        subscriptionsForDay.push({
          ...sub,
          isRecurring: false,
          displayName: sub.name
        });
      }
      
      // If recurring reminders are enabled, generate future dates
      if (sub.enable_recurring_reminders) {
        let currentRenewalDate = new Date(sub.next_renewal_date);
        let checkDate = new Date(currentRenewalDate);
        
        // Generate future dates up to 5 years from subscription start
        const subscriptionStartDate = new Date(sub.next_renewal_date);
        const fiveYearsFromStart = new Date(subscriptionStartDate.getTime() + 5 * 365 * 24 * 60 * 60 * 1000);
        
        while (checkDate <= fiveYearsFromStart) {
          // Calculate next renewal based on billing cycle using smart date handling
          // Pass the original subscription start date as first parameter, current renewal date as third parameter
          checkDate = calculateNextRenewalDate(new Date(sub.start_date), sub.billing_cycle as BillingCycle, checkDate);
          
          // If this calculated date matches our target date, add it
          if (isSameDay(checkDate, date)) {
            subscriptionsForDay.push({
              ...sub,
              isRecurring: true,
              displayName: sub.name
            });
            break;
          }
        }
      }
    });
    
    return subscriptionsForDay;
  };

  const getTrialEndingForDay = (date: Date) => {
    return subscriptions?.filter(sub => 
      sub.is_trial && 
      sub.trial_end_date &&
      isSameDay(new Date(sub.trial_end_date), date)
    ) || [];
  };



  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    const fiveYearsFromNow = addYears(new Date(), 5);
    const nextMonth = addMonths(currentDate, 1);
    
    // Only allow navigation if we're not going beyond 5 years from now
    if (nextMonth <= fiveYearsFromNow) {
      setCurrentDate(nextMonth);
    }
  };

  const handleDateClick = (date: Date) => {
    const subscriptionsForDay = getSubscriptionsForDay(date);
    setSelectedDate(date);
    setSelectedDateSubscriptions(subscriptionsForDay);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedDate(null);
    setSelectedDateSubscriptions([]);
  };

  const handleSubscriptionUpdate = () => {
    // Invalidate and refetch subscriptions to update the calendar
    queryClient.invalidateQueries(['subscriptions', user?.id]);
  };

  const getDayStyle = (date: Date) => {
    const subscriptions = getSubscriptionsForDay(date);
    const trials = getTrialEndingForDay(date);
    
    if (trials.length > 0) {
      return {
        backgroundColor: 'rgba(255, 193, 7, 0.2)',
        border: '2px solid #ffc107',
      };
    }
    
    if (subscriptions.length > 0) {
      // Check if any are recurring
      const hasRecurring = subscriptions.some(sub => sub.isRecurring);
      const hasNextPayment = subscriptions.some(sub => !sub.isRecurring);
      
      if (hasRecurring && hasNextPayment) {
        // Both recurring and next payment on same day
        return {
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          border: '2px solid #9c27b0',
        };
      } else if (hasRecurring) {
        // Only recurring payments
        return {
          backgroundColor: 'rgba(156, 39, 176, 0.1)',
          border: '2px solid #9c27b0',
        };
      } else {
        // Only next payment
        return {
          backgroundColor: 'rgba(25, 118, 210, 0.1)',
          border: '2px solid #1976d2',
        };
      }
    }
    
    return {};
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Calendar View
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your upcoming payments and trial end dates in a monthly calendar.
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: { xs: '12px 0 0 0', sm: 3 } }}>
        {/* Calendar Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 3 } }}>
          <IconButton onClick={handlePreviousMonth}>
            <ChevronLeft />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h2">
              {format(currentDate, 'MMMM yyyy')}
            </Typography>
            
            <Button
              onClick={() => setCurrentDate(new Date())}
              size="small"
              startIcon={<Event />}
              variant="contained"
              sx={{ 
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              Today
            </Button>
          </Box>
          
          <IconButton 
            onClick={handleNextMonth}
            disabled={addMonths(currentDate, 1) > addYears(new Date(), 5)}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Calendar Grid */}
        <Grid container spacing={0}>
          {/* Day Headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid item xs={12/7} key={day}>
              <Box
                sx={{
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {day}
              </Box>
            </Grid>
          ))}

          {/* Calendar Days */}
          {calendarDays.map((date, index) => {
            if (date === null) {
              return (
                <Grid item xs={12/7} key={`empty-${index}`}>
                  <Box sx={{ 
                    minHeight: 120, 
                    p: 1, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    backgroundColor: 'grey.300',
                    opacity: 0.7
                  }} />
                </Grid>
              );
            }

            const subscriptions = getSubscriptionsForDay(date);
            const trials = getTrialEndingForDay(date);
            const isCurrentMonth = isSameMonth(date, currentDate);
            const totalPayments = subscriptions.length + trials.length;
            const hasPayments = totalPayments > 0;
            
            return (
              <Grid item xs={12/7} key={date.toISOString()}>
                <Box
                  onClick={() => handleDateClick(date)}
                  sx={{
                    minHeight: 120,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: isCurrentMonth ? 'background.paper' : 'grey.50',
                    color: isCurrentMonth ? 'text.primary' : 'text.disabled',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: isCurrentMonth ? 'action.hover' : 'grey.100',
                      transform: 'scale(1.02)',
                      boxShadow: 2,
                    },
                    ...getDayStyle(date),
                  }}
                >
                  {/* Payment Count Badge */}
                  {hasPayments && (
                    <Badge
                      badgeContent={totalPayments}
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        '& .MuiBadge-badge': {
                          fontSize: '0.7rem',
                          minWidth: '20px',
                          height: '20px',
                        },
                      }}
                    />
                  )}
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {format(date, 'd')}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {subscriptions.map((sub) => (
                      <Box
                        key={`${sub.id}-${sub.isRecurring ? 'recurring' : 'next'}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: getCategoryColor(sub.category),
                          borderRadius: 1,
                          p: 0.5,
                          minHeight: { xs: 24, sm: 32 },
                        }}
                      >
                        {/* Service Logo - Always visible */}
                        <ServiceLogo
                          serviceName={sub.name}
                          category={sub.category}
                          size="small"
                        />
                        
                        {/* Subscription Name - Hidden on mobile, visible on desktop */}
                        <Typography
                          sx={{
                            color: 'white',
                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                            fontWeight: 'bold',
                            display: { xs: 'none', sm: 'block' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {sub.displayName}
                        </Typography>
                      </Box>
                    ))}
                    
                    {trials.map((trial) => (
                      <Box
                        key={trial.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          backgroundColor: '#ffc107',
                          borderRadius: 1,
                          p: 0.5,
                          minHeight: { xs: 24, sm: 32 },
                        }}
                      >
                        {/* Service Logo - Always visible */}
                        <ServiceLogo
                          serviceName={trial.name}
                          category={trial.category}
                          size="small"
                        />
                        
                        {/* Trial Label - Hidden on mobile, visible on desktop */}
                        <Typography
                          sx={{
                            color: 'white',
                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                            fontWeight: 'bold',
                            display: { xs: 'none', sm: 'block' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {trial.name} (Trial)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Legend */}
      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Legend
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  border: '2px solid #1976d2',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">Payment Due</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  border: '2px solid #9c27b0',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">Recurring Payment</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: 'rgba(255, 193, 7, 0.2)',
                  border: '2px solid #ffc107',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2">Trial Ending</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>
              Category Colors:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {CATEGORIES.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  size="small"
                  sx={{
                    backgroundColor: getCategoryColor(category),
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Upcoming Summary */}
      <Paper elevation={1} sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          This Month's Summary
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney color="primary" />
                                  <Typography variant="h6">
                  {(() => {
                    if (!subscriptions) return formatCurrency(0, getMainCurrency());
                    let total = 0;
                    const monthStart = startOfMonth(currentDate);
                    const monthEnd = endOfMonth(currentDate);
                    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
                    
                    daysInMonth.forEach(date => {
                      const daySubscriptions = getSubscriptionsForDay(date);
                      daySubscriptions.forEach(sub => {
                        // For now, use static conversion until we implement async properly
                        const convertedCost = getMonthlyCostInCurrency(sub, getMainCurrency());
                        total += convertedCost;
                      });
                    });
                    
                    return formatCurrency(total, getMainCurrency());
                  })()}
                </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Payments This Month (Including Recurring)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Event color="primary" />
                  <Typography variant="h6">
                    {(() => {
                      if (!subscriptions) return 0;
                      let count = 0;
                      const monthStart = startOfMonth(currentDate);
                      const monthEnd = endOfMonth(currentDate);
                      const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
                      
                      daysInMonth.forEach(date => {
                        const daySubscriptions = getSubscriptionsForDay(date);
                        count += daySubscriptions.length;
                      });
                      
                      return count;
                    })()}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Total Payments This Month (Including Recurring)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning color="warning" />
                  <Typography variant="h6">
                    {subscriptions?.filter(sub => 
                      sub.is_trial && 
                      sub.trial_end_date &&
                      isSameMonth(new Date(sub.trial_end_date), currentDate)
                    ).length || 0}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Trials Ending This Month
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendar Date Modal */}
      <CalendarDateModal
        open={modalOpen}
        onClose={handleModalClose}
        selectedDate={selectedDate}
        subscriptions={selectedDateSubscriptions}
        onSubscriptionUpdate={handleSubscriptionUpdate}
      />
    </Container>
  );
};

export default Calendar; 